import React from 'react';
import PropTypes from 'prop-types';
import {
  formatValue,
  WIDE_CARD_ENTRY_THRESHOLD,
  COMPOSITE_DOOR_GEARING_GROUP,
  HARDWARE_PACKING_GROUP,
  COMPOSITE_DOOR_PACKING_GROUP
} from './outputPerformanceShared';
import { StationEntry } from './StationEntry';
import { useStationData, useAreaTotal } from './useStationData';

function StationCard({ areaName, groupName, stationConfig, metricType, onSelectStation }) {
  const data = useStationData(metricType, stationConfig);

  const station = {
    name: stationConfig.name,
    stationPath: stationConfig.stationPath,
    displayType: stationConfig.displayType,
    metricType,
    isResolved: stationConfig.isResolved !== false || (metricType === 'compositeDoorGearing' && data.records.length > 0),
    resolutionNote: stationConfig.resolutionNote || null,
    ...data
  };

  const entry = {
    label: station.name,
    value: data.total,
    hasBoxData: data.hasBoxData,
    itemsCount: data.itemsCount || 0,
    boxesCount: data.boxesCount || 0,
    hasWeldedCillData: data.hasWeldedCillData,
    weldedCillBoxesCreated: data.weldedCillBoxesCreated || 0,
    weldedCillTotalFacets: data.weldedCillTotalFacets || 0,
    weldedCillFacetsLabel: data.weldedCillFacetsLabel,
    itemTypeBreakdown: data.itemTypeBreakdown || null,
    isResolved: station.isResolved,
    station
  };

  return (
    <StationEntry
      entry={entry}
      onClick={() => onSelectStation({ areaName, groupName, station })}
    />
  );
}

StationCard.propTypes = {
  areaName: PropTypes.string.isRequired,
  groupName: PropTypes.string.isRequired,
  stationConfig: PropTypes.object.isRequired,
  metricType: PropTypes.string.isRequired,
  onSelectStation: PropTypes.func.isRequired
};

const getEffectiveMetricType = (groupConfig) => {
  if (groupConfig.name === COMPOSITE_DOOR_GEARING_GROUP) return 'compositeDoorGearing';
  if (groupConfig.name === HARDWARE_PACKING_GROUP) return 'hardwarePacking';
  if (groupConfig.name === COMPOSITE_DOOR_PACKING_GROUP) return 'compositeDoorPacking';
  return groupConfig.metricType || 'items';
};

function GroupCard({ areaName, groupConfig, onSelectStation }) {
  const metricType = getEffectiveMetricType(groupConfig);
  const stations = groupConfig.stations || [];
  const isWide = stations.length >= WIDE_CARD_ENTRY_THRESHOLD;

  const stationCards = stations.map(sc => (
    <StationCard
      key={sc.name}
      areaName={areaName}
      groupName={groupConfig.name}
      stationConfig={sc}
      metricType={metricType}
      onSelectStation={onSelectStation}
    />
  ));

  if (isWide) {
    const splitIndex = Math.ceil(stationCards.length / 2);
    return (
      <div className="output-benchmark-card output-benchmark-card--wide">
        <div className="output-benchmark-card-title">{groupConfig.name}</div>
        <div className="output-benchmark-wide-grid">
          <div className="output-benchmark-entry-grid is-two-col">
            {stationCards.slice(0, splitIndex)}
          </div>
          <div className="output-benchmark-entry-grid is-two-col">
            {stationCards.slice(splitIndex)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="output-benchmark-card output-benchmark-card--list">
      <div className="output-benchmark-card-title">{groupConfig.name}</div>
      <div className="output-benchmark-entry-grid is-two-col">
        {stationCards}
      </div>
    </div>
  );
}

GroupCard.propTypes = {
  areaName: PropTypes.string.isRequired,
  groupConfig: PropTypes.object.isRequired,
  onSelectStation: PropTypes.func.isRequired
};

export function AreaSection({ areaConfig, onSelectStation }) {
  const areaTotal = useAreaTotal(areaConfig);

  return (
    <section className="output-area-section">
      <div className="output-area-section-header">
        <div className="output-area-section-title">{areaConfig.name}</div>
        <div className="output-area-section-total">
          <span>Total</span>
          <strong>{formatValue(areaTotal)}</strong>
        </div>
      </div>

      <div className="output-benchmark-grid output-benchmark-grid--groups">
        {(areaConfig.groups || []).map(groupConfig => (
          <GroupCard
            key={groupConfig.name}
            areaName={areaConfig.name}
            groupConfig={groupConfig}
            onSelectStation={onSelectStation}
          />
        ))}
      </div>
    </section>
  );
}

AreaSection.propTypes = {
  areaConfig: PropTypes.object.isRequired,
  onSelectStation: PropTypes.func.isRequired
};
